import type { EdgeTypes, NodeChange, NodeTypes } from '@xyflow/react';
import { applyNodeChanges, ReactFlow } from '@xyflow/react';
import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { PartnerNode as PartnerNodeType } from '../../app/api/generated/client';
import { useUpdateNodePosition } from '../api/mutations';
import { connectorNodeSize, partnerNodeSize } from '../config/constants';
import { useFamiliesTreeElkLayout, useFamilyTreeNodesEdges, useHelperLines, useVisibleNodesAndEdges } from '../hooks';
import type { FamilyTreeNode } from '../interfaces';
import '../styles/main.css';
import { FamilyTreeEdge } from './family-tree-edge';
import { FamilyTreeHeader } from './family-tree-header';
import { FamilyTreeWarningDialog } from './family-tree-warning-dialog';
import { ConnectorNode, HelperLineNode, NoRelationsNode, PartnerNode } from './nodes';
import { ZoomControls } from './zoom-controls';

const nodeTypes: NodeTypes = {
    familyTreeNode: PartnerNode,
    familyTreeConnectorNode: ConnectorNode,
    familyTreeNoRelationsNode: NoRelationsNode,
    helperLineNode: HelperLineNode,
};

const edgeTypes: EdgeTypes = {
    familyTreeEdge: FamilyTreeEdge,
};

interface IProps {
    partnerUuid: string;
    isPresentationMode?: boolean;
    setPresentationMode: (isPresentationModeOn: boolean) => void;
    isPrintMode?: boolean;
}

export const FamilyTree: FunctionComponent<IProps> = ({ partnerUuid, isPresentationMode = false, setPresentationMode, isPrintMode = false }) => {
    const [familyTreeNodes, setFamilyTreeNodes] = useState<FamilyTreeNode[]>([]);
    const [autoPositionNodes, setAutoPositionNodes] = useState<boolean>(false);

    const { nodes, edges, isLoadingFamilyTree, noRelationsNode, hasRelations } = useFamilyTreeNodesEdges(partnerUuid, isPresentationMode, isPrintMode);
    const { data: layout } = useFamiliesTreeElkLayout(
        nodes.map((n) => {
            const { nodePosition } = n.data as PartnerNodeType;
            const position = nodePosition != null ? { x: nodePosition.x, y: nodePosition.y } : n.position;

            return { ...n, position };
        }),
        edges,
        autoPositionNodes,
    );
    const { visibleNodes, visibleEdges } = useVisibleNodesAndEdges(familyTreeNodes, layout?.enrichedEdges ?? [], isPresentationMode);

    const { mutate: updateNodePosition } = useUpdateNodePosition(partnerUuid);

    const { showHelperLines, removeHelperLines, snap } = useHelperLines(setFamilyTreeNodes);

    const getSnapPosition = useCallback(
        (node: FamilyTreeNode) => {
            const width = node.data.isConnector ? connectorNodeSize.width : partnerNodeSize.width;
            const height = node.data.isConnector ? connectorNodeSize.height : partnerNodeSize.height;

            return {
                x: snap.x != null ? snap.x - width / 2 : node.position.x,
                y: snap.y != null ? snap.y - height / 2 : node.position.y,
            };
        },
        [snap],
    );

    useEffect(() => {
        if (layout?.enrichedNodes.length && hasRelations) {
            setFamilyTreeNodes(layout.enrichedNodes);
        } else if (noRelationsNode) {
            setFamilyTreeNodes([noRelationsNode]);
        }
    }, [layout, hasRelations, noRelationsNode, autoPositionNodes, setAutoPositionNodes]);

    const onNodesChange = useCallback((changes: NodeChange<FamilyTreeNode>[]) => {
        setFamilyTreeNodes((nds) => applyNodeChanges<FamilyTreeNode>(changes, nds));
    }, []);

    const onNodeDrag = useCallback(
        (_: MouseEvent, node: FamilyTreeNode) => {
            showHelperLines(node, familyTreeNodes);
            setFamilyTreeNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, position: getSnapPosition(n) } : n)));
        },
        [familyTreeNodes, showHelperLines, getSnapPosition],
    );

    const onNodeDragStop = useCallback(
        (_: MouseEvent, node: FamilyTreeNode) => {
            const snappedPosition = getSnapPosition(node);

            updateNodePosition({ nodeUuid: node.id, newPosition: snappedPosition, isConnectorNode: node.data.isConnector });
            setFamilyTreeNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, position: snappedPosition } : n)));

            removeHelperLines();
        },
        [getSnapPosition, removeHelperLines, updateNodePosition],
    );

    if (isLoadingFamilyTree || !familyTreeNodes.length) return <ZfLoadingSpinner size="extra-large" style={{ margin: 'auto' }} />;

    return (
        <div className="family-tree">
            <FamilyTreeHeader partnerUuid={partnerUuid} nodes={nodes} />
            <ReactFlow
                style={{ flex: 1 }}
                nodes={visibleNodes}
                nodeTypes={nodeTypes}
                edges={visibleEdges}
                edgeTypes={edgeTypes}
                fitView={true}
                nodesDraggable={true}
                panOnDrag={hasRelations}
                proOptions={{ hideAttribution: true }}
                onNodesChange={onNodesChange}
                onNodeDrag={onNodeDrag}
                onNodeDragStop={onNodeDragStop}
            >
                <ZoomControls setAutoPositionNodes={setAutoPositionNodes} partnerUuid={partnerUuid} />
            </ReactFlow>
            <FamilyTreeWarningDialog isPresentationMode={isPresentationMode} setPresentationMode={setPresentationMode} />
        </div>
    );
};
